/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import type { ReactNode } from 'react';

import { CheckboxInput, Typography2 } from '@coursera/cds-core';

import _t from 'i18n!nls/enterprise-ui';

const styles = {
  bar: css`
    background-color: var(--cds-color-emphasis-primary-background-strong);
    padding: var(--cds-spacing-150) var(--cds-spacing-300) var(--cds-spacing-150) 0;
    border-radius: var(--cds-border-radius-100) var(--cds-border-radius-100) 0 0;
    width: 100%;
    min-height: 60px;
    display: flex;
    align-items: center;
    position: sticky;
    top: 66px;
    z-index: 4;
  `,
  // Matches ActionTable's fixed 72px checkbox column so this bar's checkbox (and the label
  // right after it) line up with the checkbox and name column in the rows below.
  checkboxColumn: css`
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 72px;
    max-width: 72px;
    width: 72px;
    flex-shrink: 0;
  `,
  label: css`
    margin-left: var(--cds-spacing-300);
  `,
  actions: css`
    display: flex;
    gap: var(--cds-spacing-50);
    align-items: center;
    margin-left: auto;
  `,
};

type Props = {
  /** Text describing the current selection (e.g. "3 groups selected" or "All groups selected"). */
  label: string;
  /** When true, the checkbox renders as fully checked; otherwise indeterminate (partial selection). */
  selectAll: boolean;
  /** Called when the user clicks the checkbox to clear the selection. */
  onUnselect: () => void;
  /** Action buttons to render on the right side of the bar (e.g. "Send message", "Assign content"). */
  children?: ReactNode;
};

/**
 * A sticky action bar displayed above the table header when one or more rows are selected.
 *
 * Renders a checked/indeterminate checkbox (reflecting whether all or some rows are selected),
 * a selection count label, and a slot for contextual action buttons on the right.
 * Intended to be composed inside `ActionTable`'s `bulkActions` prop.
 *
 * @example
 * ```tsx
 * <ActionTable
 *   ...
 *   bulkActions={
 *     <BulkActionBar
 *       label={selectAll ? 'All groups selected' : `${selectedKeys.size} groups selected`}
 *       selectAll={selectAll}
 *       onUnselect={() => { setSelectedKeys(new Set()); setSelectAll(false); }}
 *     >
 *       <Button variant="ghostInvert" size="small">Send message</Button>
 *     </BulkActionBar>
 *   }
 * />
 * ```
 */
export const BulkActionBar = ({ label, selectAll, onUnselect, children }: Props) => {
  return (
    <div css={styles.bar}>
      <div css={styles.checkboxColumn}>
        <CheckboxInput
          checked
          indeterminate={!selectAll}
          aria-label={selectAll ? _t('Deselect all rows') : _t('Deselect selected rows')}
          onChange={onUnselect}
        />
      </div>
      <Typography2 component="span" variant="actionPrimary" color="invertBody" css={styles.label}>
        {label}
      </Typography2>
      {children && <div css={styles.actions}>{children}</div>}
    </div>
  );
};
